import React, { useCallback, useContext, useRef } from 'react';
import {
  SurveyContext,
  RegisterSurveyItemContainerRefContext,
  TriggerScrollToSurveyItemContext,
  RegisterCollapsibleSectionContext,
} from './Contexts';
import { parsePathSegment } from '../util/SurveyResponse';

export type SurveyItemRefsProviderOwnProps = {};

export type SurveyItemRefsProviderProps = SurveyItemRefsProviderOwnProps;

type SurveyItemRefDict = Record<string, HTMLDivElement>;
type SectionExpandDict = Record<string, () => void>;

const SurveyItemRefsProvider: React.FC<SurveyItemRefsProviderProps> = props => {
  const refDict = useRef<SurveyItemRefDict>({});
  const sectionExpandDict = useRef<SectionExpandDict>({});
  const survey = useContext(SurveyContext);

  const registerSurveyItemContainerRef = useCallback(
    (linkId: string, ref: HTMLDivElement | null) => {
      if (!linkId) return;

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      refDict.current[linkId] = ref;
    },
    [],
  );

  const registerCollapsibleSection = useCallback(
    (linkId: string, expand: (() => void) | null) => {
      if (expand) {
        sectionExpandDict.current[linkId] = expand;
      } else {
        delete sectionExpandDict.current[linkId];
      }
    },
    [],
  );

  const triggerScrollToSurveyItem = useCallback(
    (fieldName: string) => {
      // target link ID may be nested for group items.  Scroll to the container in the path
      const possibleScrollPaths = survey.fieldNameToUpdatePath(fieldName);
      const instanceAncestors: Array<{
        groupLink: string;
        instanceIndex: number;
      }> = [];
      possibleScrollPaths.forEach(pathSegment => {
        const { link, instanceIndex } = parsePathSegment(pathSegment);
        if (instanceIndex !== null) {
          instanceAncestors.push({ groupLink: link, instanceIndex });
        }
        const sectionKey = survey.getSectionCompletionKey(
          link,
          instanceAncestors,
        );
        sectionExpandDict.current[sectionKey]?.();
      });

      const possibleScrollLinks = possibleScrollPaths.map(
        pathSegment => parsePathSegment(pathSegment).link,
      );
      const targetLinkId = [...possibleScrollLinks]
        .reverse()
        .find(link => refDict.current[link]);
      if (!targetLinkId) {
        console.warn(
          `Unable to scroll to the item with link: "${targetLinkId}"`,
        );
        return;
      }

      const targetRef = refDict.current[targetLinkId];
      setTimeout(
        () =>
          targetRef?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          }),
        300,
      );
    },
    [refDict, survey],
  );

  return (
    <RegisterSurveyItemContainerRefContext.Provider
      value={registerSurveyItemContainerRef}
    >
      <RegisterCollapsibleSectionContext.Provider
        value={registerCollapsibleSection}
      >
        <TriggerScrollToSurveyItemContext.Provider
          value={triggerScrollToSurveyItem}
        >
          {props.children}
        </TriggerScrollToSurveyItemContext.Provider>
      </RegisterCollapsibleSectionContext.Provider>
    </RegisterSurveyItemContainerRefContext.Provider>
  );
};

export default SurveyItemRefsProvider;
